import { TradingLoadingButton, TradingTextField } from '@trading-ui/core';
import TradingSelect from '@trading-ui/select';
import { useTranslation } from 'react-i18next';

import type { CreatePlantPayload, User } from '@/interfaces';
import type { PricingIndex } from '@/api/services/settings/pricing-indices.service';

import { FormUtils } from '../../utils/form.utils';

import * as styles from './Form.css';

type Props = {
  form: CreatePlantPayload;
  isSubmitting: boolean;
  errors: Partial<Record<keyof CreatePlantPayload, string>>;
  updateField: (field: keyof CreatePlantPayload, value: string | number | null) => void;
  updateUsers: (users: { id: number; value: string; label: string }[]) => void;
  handleSubmit: () => void;
  companyUsers: User[];
  pricingIndices: PricingIndex[];
  type: 'create' | 'update';
};

export default function CompanyCupsForm({
  form,
  isSubmitting,
  errors,
  companyUsers,
  pricingIndices,
  updateField,
  updateUsers,
  handleSubmit,
  type,
}: Props) {
  const { t } = useTranslation();

  const pricingIndicesOptions = FormUtils.getPricingIndicesOptions(pricingIndices);
  const selectedPricingIndex = pricingIndicesOptions.find((opt) => opt.id === form.pricingIndexId) ?? null;

  return (
    <div className={styles.formContainer}>
      <TradingTextField
        label={t('settings.companyDetailsTab.companyCups.modal.identifierLabel')}
        value={form.identifier}
        onChange={(e) => updateField('identifier', e.target.value)}
        error={!!errors.identifier}
        helperText={errors.identifier}
        disabled={type === 'update'}
      />
      <TradingTextField
        label={t('settings.companyDetailsTab.companyCups.modal.aliasLabel')}
        value={form.alias}
        onChange={(e) => updateField('alias', e.target.value)}
        error={!!errors.alias}
        helperText={errors.alias}
      />
      <TradingSelect
        label={t('settings.companyDetailsTab.companyCups.modal.usersLabel')}
        options={FormUtils.getUsersOptions(companyUsers)}
        value={form.users}
        onChange={(value) => updateUsers(value)}
        multiple
        style={{ width: '400px' }}
      />
      <TradingSelect
        label={t('settings.companyDetailsTab.companyCups.modal.pricingIndexLabel')}
        options={pricingIndicesOptions}
        value={selectedPricingIndex}
        onChange={(value) => updateField('pricingIndexId', value?.id ?? null)}
        style={{ width: '400px' }}
      />
      <TradingLoadingButton
        label={
          type === 'create'
            ? t('settings.companyDetailsTab.companyCups.modal.createCupsButton')
            : t('settings.companyDetailsTab.companyCups.modal.updateCupsButton')
        }
        handleClick={handleSubmit}
        className={styles.button}
        loading={isSubmitting}
      />
    </div>
  );
}
